import type { Board, Coupon } from '../../../../schema/coupon/entry.js';
import type { Definition } from '../../../../schema/frameworks/entry.js';
import { getSystemFromNumber } from '../systems/getSystemFromNumber.js';
import type { SystemKeyTable } from '../systems/systemsData.js';
import { getEventCount } from './getEventCount.js';
import { selectSystemSize } from './selectSystemSize.js';

type QueryOptionsType = {
  definition: Definition;
};

const getBoardMOutcomes = (outcomes: Board['outcomes'], mOutcomes: Board['mOutcomes']): number[] =>
  outcomes.map((outcomeGroup) => outcomeGroup.filter(Boolean).length).filter((dummy, i) => mOutcomes[i]);

/**
 * Selects the price for the given board.
 *
 * @param board Coupon board entity
 * @param coupon Coupon entity
 * @param root0 Object containing dependencies
 * @param root0.definition Product definition
 * @returns total price of board
 */
function calculateBoardPrice(board: Board, coupon: Coupon, { definition }: QueryOptionsType): number {
  const systemSize = selectSystemSize(board, { definition });
  const week = definition.weeks[coupon.week] ?? 1;

  let basePrice = 0;

  if (coupon.selectedSystem === 'REDUCERA_FRITT' && coupon.reduceModeCount) {
    basePrice = coupon.reduceModeCount;
  } else if (
    (coupon.rSystem && coupon.selectedSystem === 'RSYS') ||
    (coupon.uSystem && coupon.selectedSystem === 'USYS')
  ) {
    let selectedSystem: SystemKeyTable | undefined;
    if (coupon.rSystem && coupon.selectedSystem === 'RSYS') {
      selectedSystem = getSystemFromNumber(coupon.rSystem);
    }
    if (coupon.uSystem && coupon.selectedSystem === 'USYS') {
      selectedSystem = getSystemFromNumber(coupon.uSystem);
    }

    if (selectedSystem) {
      const couponMOutcomes = getBoardMOutcomes(board.outcomes, board.mOutcomes);

      basePrice = couponMOutcomes.reduce((amount, sum) => amount * sum, selectedSystem.rows.length);
    }
  } else {
    basePrice = coupon.betAmount * systemSize;
  }

  return basePrice * week;
}

/**
 * Calculates price for the given coupon.
 *
 * @param coupon Coupon entity
 * @param root0 Object containing dependencies
 * @param root0.definition Product definition
 * @returns total coupon price
 */
export function calculateCouponPrice(coupon: Coupon, { definition }: QueryOptionsType): number {
  const isMultiBoard = coupon.boards.length > 1;

  // The old "hack" were to get the board price from a pre-calculated property "preCalculatedSystemSize" from the API
  if (coupon.couponType === 'external_system') {
    return 0;
  }

  const priceOfAllBoards = coupon.boards.reduce((sum, board) => {
    const [startedEventCount, totalEventCount] = getEventCount(board);
    const isEveryMatchSelected = startedEventCount === totalEventCount;
    if (isMultiBoard && !isEveryMatchSelected) {
      return sum;
    }

    return sum + calculateBoardPrice(board, coupon, { definition });
  }, 0);

  return priceOfAllBoards;
}
